import {
  Ledger,
  LedgerPosting,
  LedgerTransaction,
  LedgerReportParams,
  PackagePostingReq,
  ReversalActionReq,
} from "../types/ledger.types";
import { callApi } from "./api";

const endpoints = {
  getAllLedgers: "Geacloud_Ledgercreates/getAllLedger_branch_geacloud",
  postLedgerAccount: "Geacloud_Ledgers/postLedgerAccount_geacloud",
  getAllPostingReport: "Geacloud_Ledgers/getAllPostingReport_geacloud",
  getPostingReport: "Geacloud_Ledgers/getMyPostingReport_geacloud",
  postPackageAccount: "Geacloud_Ledgers/postPackageAccount_geacloud",
  getPostingsByPostCode: "Geacloud_Ledgers/getPostingByPostCode_geacloud",
  initiateReversal: "Geacloud_Ledgers/createReversalRequest_geacloud",
  getAllReversalRequests: "Geacloud_Ledgers/listReversalRequests_geacloud",
  reversalAction: "Geacloud_Ledgers/approveOrReject_geacloud",
};

export const ledgerService = {
  getAllLedgers: async (): Promise<Ledger[]> => {
    try {
      const response = await callApi(endpoints.getAllLedgers, {});
      const ledgers = response?.data?.data || [];

      const seen = new Set<string>();
      const resp = ledgers.filter((ledger: Ledger) => {
        if (!ledger.sn) return false;
        if (seen.has(ledger.sn)) return false;
        seen.add(ledger.sn);
        return true;
      });
      return resp;
    } catch (error) {
      console.error("Error fetching ledgers:", error);
      return [];
    }
  },

  postLedgerAccount: async (posting: LedgerPosting): Promise<any> => {
    try {
      return await callApi(endpoints.postLedgerAccount, posting);
    } catch (error) {
      console.error("Error posting ledger account:", error);
      throw error;
    }
  },

  getPostingReport: async (
    params: LedgerReportParams
  ): Promise<LedgerTransaction[]> => {
    try {
      const response = await callApi(endpoints.getPostingReport, {
        startDate: params.startDate,
        endDate: params.endDate,
      });
      const data = response?.data?.data || [];
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Error fetching posting report:", error);
      throw error;
    }
  },

  getAllPostingReport: async (
    params: LedgerReportParams
  ): Promise<LedgerTransaction[]> => {
    try {
      const response = await callApi(endpoints.getAllPostingReport, {
        startDate: params.startDate,
        endDate: params.endDate,
      });
      const data = response?.data?.data || [];
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Error fetching all posting report:", error);
      throw error;
    }
  },

  getPostingsByPostCode: async (
    postCode: string
  ): Promise<LedgerTransaction[]> => {
    try {
      const response = await callApi(endpoints.getPostingsByPostCode, {
        postCode,
      });
      const data = response?.data?.data || [];
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Error fetching postings by post code:", error);
      throw error;
    }
  },

  postPackageAccount: async (posting: PackagePostingReq): Promise<any> => {
    try {
      const accounts = posting.accounts.map((acc) => ({
        sn: acc.sn,
        amount: acc.amount,
        transType: acc.transType,
      }));

      const payload = {
        accounts: accounts,
        desp: posting.desp,
        reference: posting.reference || "",
        referenceDetail: posting.referenceDetail || "",
      };

      return await callApi(endpoints.postPackageAccount, payload);
    } catch (error) {
      console.error("Error posting package account:", error);
      throw error;
    }
  },

  initiateReversal: async (postCode: string): Promise<any> => {
    try {
      return await callApi(endpoints.initiateReversal, { postCode });
    } catch (error) {
      console.error("Error initiating reversal:", error);
      throw error;
    }
  },

  getAllReversalRequests: async (params?: {
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<any> => {
    try {
      const payload: any = {};
      if (params?.status) payload.status = params.status;
      if (params?.startDate) payload.startDate = params.startDate;
      if (params?.endDate) payload.endDate = params.endDate;
      const response = await callApi(endpoints.getAllReversalRequests, payload);
      const data = response?.data?.data || [];
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Error fetching reversal requests:", error);
      throw error;
    }
  },

  approveReversal: async (req: ReversalActionReq): Promise<any> => {
    try {
      return await callApi(endpoints.reversalAction, req);
    } catch (error) {
      console.error("Error approving reversal:", error);
      throw error;
    }
  },

  rejectReversal: async (req: ReversalActionReq): Promise<any> => {
    try {
      return await callApi(endpoints.reversalAction, req);
    } catch (error) {
      console.error("Error rejecting reversal:", error);
      throw error;
    }
  },
};
